/**
 * WorkloadHeatMap Component
 *
 * GitHub-contributions style heat map showing task density.
 * Used in both profile and team views.
 *
 * - compact=true: 7 days (current week), smaller cells, no labels
 * - compact=false: 90-day grid (13 weeks x 7 days), with labels
 */

import { useMemo } from 'react';
import {
  subDays,
  startOfWeek,
  addDays,
  isSameDay,
  format,
} from 'date-fns';
import { it } from 'date-fns/locale';
import clsx from 'clsx';
import type { Task } from '@/types';

export interface WorkloadHeatMapProps {
  tasks: Task[];
  days?: number; // default 90
  compact?: boolean; // default false, for use in MemberCard
}

// Day-of-week labels (Italian)
const dayLabels = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

function getColorClass(count: number): string {
  if (count === 0) return 'bg-surface-tertiary/40';
  if (count <= 1) return 'bg-accent-emerald/30';
  if (count <= 3) return 'bg-accent-emerald/60';
  if (count <= 5) return 'bg-accent-amber/50';
  return 'bg-accent-rose/50';
}

export function WorkloadHeatMap({ tasks, days = 90, compact = false }: WorkloadHeatMapProps) {
  const today = useMemo(() => new Date(), []);

  // Build the grid of days
  const gridData = useMemo(() => {
    if (compact) {
      // Show current week (Mon-Sun)
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekDays: Date[] = [];
      for (let i = 0; i < 7; i++) {
        weekDays.push(addDays(weekStart, i));
      }
      return { dates: weekDays, weeks: 1 };
    }

    // Full grid: go back `days` days, align to week start
    const startDate = startOfWeek(subDays(today, days), { weekStartsOn: 1 });
    const endDate = today;
    const allDates: Date[] = [];
    let current = startDate;
    while (current <= endDate) {
      allDates.push(current);
      current = addDays(current, 1);
    }
    // Pad to complete the last week
    while (allDates.length % 7 !== 0) {
      allDates.push(addDays(allDates[allDates.length - 1], 1));
    }
    return { dates: allDates, weeks: Math.ceil(allDates.length / 7) };
  }, [today, days, compact]);

  // Count tasks per day
  const taskCountMap = useMemo(() => {
    const map = new Map<string, number>();

    tasks.forEach((task) => {
      const dates: number[] = [];
      if (task.dueDate) dates.push(task.dueDate);
      if (task.scheduledStart) dates.push(task.scheduledStart);

      dates.forEach((ts) => {
        const d = new Date(ts);
        // Build a key as YYYY-MM-DD for matching
        const key = format(d, 'yyyy-MM-dd');
        map.set(key, (map.get(key) || 0) + 1);
      });
    });

    return map;
  }, [tasks]);

  // Get count for a date
  const getCount = (date: Date): number => {
    const key = format(date, 'yyyy-MM-dd');
    return taskCountMap.get(key) || 0;
  };

  // Month labels for full grid
  const monthLabels = useMemo(() => {
    if (compact) return [];

    const labels: { label: string; weekIndex: number }[] = [];
    let lastMonth = -1;

    for (let i = 0; i < gridData.dates.length; i += 7) {
      const weekDate = gridData.dates[i];
      const month = weekDate.getMonth();
      if (month !== lastMonth) {
        labels.push({
          label: format(weekDate, 'MMM', { locale: it }),
          weekIndex: Math.floor(i / 7),
        });
        lastMonth = month;
      }
    }

    return labels;
  }, [compact, gridData.dates]);

  const cellSize = compact ? 8 : 12;
  const cellGap = compact ? 2 : 3;

  if (compact) {
    // Compact: single row of 7 cells with day labels
    return (
      <div className="flex items-center gap-1">
        {gridData.dates.map((date, i) => {
          const count = getCount(date);
          const isToday = isSameDay(date, today);
          return (
            <div key={date.toISOString()} className="flex flex-col items-center gap-0.5">
              <span className={clsx(
                'text-[9px] leading-none',
                isToday ? 'text-accent-violet font-semibold' : 'text-text-quaternary'
              )}>
                {dayLabels[i]}
              </span>
              <div
                className={clsx(
                  'rounded-[3px] transition-colors',
                  getColorClass(count),
                  isToday && 'ring-1 ring-accent-violet/50'
                )}
                style={{ width: 14, height: 14 }}
                title={`${format(date, 'EEEE d MMM', { locale: it })}: ${count} task`}
              />
              {count > 0 && (
                <span className="text-[8px] text-text-quaternary tabular-nums leading-none">{count}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full grid: organize dates into columns (weeks) with rows (days of week)
  const weeks: Date[][] = [];
  for (let i = 0; i < gridData.dates.length; i += 7) {
    weeks.push(gridData.dates.slice(i, i + 7));
  }

  return (
    <div className="flex flex-col gap-1">
      {/* Month labels row (positioned above columns) */}
      <div className="relative" style={{ height: 14, marginLeft: 16 }}>
        {monthLabels.map((ml, idx) => (
          <span
            key={`${ml.label}-${idx}`}
            className="text-[10px] text-text-quaternary absolute"
            style={{
              left: ml.weekIndex * (cellSize + cellGap),
              top: 0,
            }}
          >
            {ml.label}
          </span>
        ))}
      </div>

      <div className="flex gap-0">
        {/* Day-of-week labels */}
        <div className="flex flex-col justify-between shrink-0" style={{ width: 16, height: 7 * (cellSize + cellGap) - cellGap }}>
          {dayLabels.map((label, i) => (
            <span
              key={i}
              className="text-[10px] text-text-quaternary leading-none"
              style={{ height: cellSize, display: 'flex', alignItems: 'center' }}
            >
              {i % 2 === 0 ? label : ''}
            </span>
          ))}
        </div>

        {/* Grid */}
        <div className="flex" style={{ gap: cellGap }}>
          {weeks.map((weekDates, weekIdx) => (
            <div key={weekIdx} className="flex flex-col" style={{ gap: cellGap }}>
              {weekDates.map((date) => {
                const count = getCount(date);
                const todayMatch = isSameDay(date, today);
                return (
                  <div
                    key={date.toISOString()}
                    className={clsx(
                      'rounded-[2px] transition-colors',
                      getColorClass(count),
                      todayMatch && 'ring-1 ring-accent-violet/60'
                    )}
                    style={{ width: cellSize, height: cellSize }}
                    title={`${format(date, 'EEEE d MMM yyyy', { locale: it })}: ${count} task`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
